// docs/50 — the fallen: bodies lying where they dropped for the rest of the fight. A body holds no
// square, so it is drawn bare (no base marker, outline, HP plate, name or badge) on the low layer,
// under the fog and under anyone who walks onto it, greyed and see-through so it never reads as a
// unit. Each lies at the angle and offset the sim seeded (`fk_sim::Fallen`), so a pile on one
// square shows every body, and every player sees the same pile.
import Phaser from 'phaser';
import type { FallenView, Snapshot, UnitState } from '../../core/sim/types';
import type { AtlasManifest } from '../../config';
import { unitFrame } from '../assets';
import { bodyScale } from '../hugeArt';

/** How see-through a body is, and how dark (a multiply on top of the desaturation). */
export const FALLEN_ALPHA = 0.62;
export const FALLEN_SHADE = 0xd6d2ca;

/** Where a body's centre lies on the board (board px) and its angle (degrees clockwise). */
export function fallenPose(f: FallenView, cell: number): { x: number; y: number; angle: number } {
  const half = (Math.max(1, f.size) * cell) / 2;
  return { x: f.tile.x * cell + half + (f.dx * cell) / 16, y: f.tile.y * cell + half + (f.dy * cell) / 16, angle: f.turn };
}

/**
 * Where a feet-pivoted figure (`UnitSprite`: origin at the bottom centre) must stand so that, turned
 * by `angle`, its body lands on `fallenPose` — what the fall tweens to.
 */
export function toppledFeet(f: FallenView, cell: number, height: number): { x: number; y: number; angle: number } {
  const p = fallenPose(f, cell);
  const r = Phaser.Math.DegToRad(p.angle);
  return { x: p.x - (height / 2) * Math.sin(r), y: p.y + (height / 2) * Math.cos(r), angle: p.angle };
}

/**
 * A greyed copy of a sprite frame, baked once into a canvas texture (`fallen:<key>:<frame>`) and
 * cached: a colour-matrix filter on the board's layers drew nothing under the board camera.
 */
export function greyFrame(scene: Phaser.Scene, key: string, frame: string | number): { key: string; frame?: string | number } {
  const k = `fallen:${key}:${frame}`;
  if (scene.textures.exists(k)) return { key: k };
  const f = scene.textures.getFrame(key, frame);
  const src = f?.source.image as CanvasImageSource | undefined;
  if (!f || !src) return { key, frame };
  const ct = scene.textures.createCanvas(k, f.cutWidth, f.cutHeight);
  if (!ct) return { key, frame };
  const ctx = ct.getContext();
  ctx.drawImage(src, f.cutX, f.cutY, f.cutWidth, f.cutHeight, 0, 0, f.cutWidth, f.cutHeight);
  const img = ctx.getImageData(0, 0, f.cutWidth, f.cutHeight), d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    // most of the colour gone, a little left so a goblin is still a goblin
    const l = 0.3 * d[i]! + 0.59 * d[i + 1]! + 0.11 * d[i + 2]!;
    d[i] = l + (d[i]! - l) * 0.15; d[i + 1] = l + (d[i + 1]! - l) * 0.15; d[i + 2] = l + (d[i + 2]! - l) * 0.15;
  }
  ctx.putImageData(img, 0, 0);
  ct.refresh();
  return { key: k };
}

export class CorpseLayer {
  private readonly root: Phaser.GameObjects.Container;
  private drawn = '';

  constructor(private readonly scene: Phaser.Scene, layer: Phaser.GameObjects.Layer, private readonly manifest: AtlasManifest) {
    this.root = scene.add.container(0, 0);
    layer.add(this.root);
  }

  /** Draws the snapshot's bodies, oldest first (the last to fall lies on top); `skip` keeps one off the ground. */
  draw(snapshot: Snapshot, skip: (u: UnitState) => boolean = () => false): void {
    const bodies = (snapshot.board.fallen ?? [])
      .map((f) => ({ f, u: snapshot.units.find((x) => x.unitId === f.unit) }))
      .filter((b): b is { f: FallenView; u: UnitState } => !!b.u && b.u.onBoard === false && !skip(b.u));
    const key = JSON.stringify(bodies.map((b) => [b.f, b.u.appearance, b.u.sprite]));
    if (key === this.drawn) return;
    this.drawn = key;
    this.root.removeAll(true);
    const cell = this.manifest.cell;
    for (const { f, u } of bodies) {
      const { key: tex, frame } = unitFrame(this.manifest, u, (k, fr) => this.scene.textures.exists(k) && this.scene.textures.get(k).has(fr));
      const grey = greyFrame(this.scene, tex, frame);
      const p = fallenPose(f, cell);
      // docs/79: a huge body lies across its footprint, drawn at its size
      this.root.add(this.scene.add.image(p.x, p.y, grey.key, grey.frame).setOrigin(0.5, 0.5).setScale(bodyScale(tex, f.size)).setAngle(p.angle).setAlpha(FALLEN_ALPHA).setTint(FALLEN_SHADE));
    }
  }

  /** The bodies drawn (for `__fkFallen` in e2e). */
  count(): number { return this.root.length; }
}
